import type { ContentBlockParam, MessageParam } from '@anthropic-ai/sdk/resources/messages';

export function chatTitle(message: string) {
  const line = message.replace(/\s+/g, ' ').trim();
  if (!line) return 'New chat';
  return line.length > 80 ? `${line.slice(0, 77)}…` : line;
}

export function cleanChatTitle(raw: string) {
  const line = raw
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^["']+|["']+$/g, '')
    .replace(/[.]+$/g, '')
    .trim();
  if (!line) return '';
  return line.length > 60 ? `${line.slice(0, 57)}…` : line;
}

function textOf(content: MessageParam['content']) {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return '';
  const parts: string[] = [];
  content.forEach((block) => {
    if (!block || typeof block !== 'object' || !('type' in block)) return;
    const visible = block.type === 'text' ? ('text' in block ? block.text : '') : block.type === 'thinking' ? ('thinking' in block ? block.thinking : '') : '';
    if (typeof visible === 'string' && visible) parts.push(visible);
  });
  return parts.join('\n');
}

export function firstExchange(messages: MessageParam[]) {
  let user = '';
  let assistant = '';
  messages.forEach((message) => {
    const text = textOf(message.content).trim();
    if (!text) return;
    if (message.role === 'user' && !user) user = text;
    if (message.role === 'assistant' && !assistant) assistant = text;
  });
  return { user, assistant };
}

export function transcriptSearchText(title: string, messages: MessageParam[]) {
  const parts = [title];
  messages.forEach((message) => {
    const text = textOf(message.content);
    if (text) parts.push(text);
  });
  return parts.join('\n');
}

function asBlocks(content: MessageParam['content']): ContentBlockParam[] {
  if (typeof content === 'string') return [{ type: 'text', text: content }];
  if (!Array.isArray(content)) return [];
  return content;
}

function toolUseIds(message: MessageParam) {
  if (message.role !== 'assistant') return [];
  return asBlocks(message.content).flatMap((block) => (block.type === 'tool_use' && block.id ? [block.id] : []));
}

function toolResultIds(message: MessageParam) {
  if (message.role !== 'user') return [];
  return asBlocks(message.content).flatMap((block) => (block.type === 'tool_result' && block.tool_use_id ? [block.tool_use_id] : []));
}

function missingToolResult(id: string) {
  return { type: 'tool_result' as const, tool_use_id: id, content: 'Tool result missing.', is_error: true };
}

function collapseRoles(messages: MessageParam[]): MessageParam[] {
  return messages.reduce<MessageParam[]>((out, message) => {
    const prev = out[out.length - 1];
    if (prev?.role !== message.role) {
      out.push(message);
      return out;
    }
    out[out.length - 1] = {
      role: message.role,
      content: [...asBlocks(prev.content), ...asBlocks(message.content)],
    };
    return out;
  }, []);
}

export function repairToolTranscript(input: MessageParam[]): MessageParam[] {
  const paired = input.reduce<{ items: MessageParam[]; skip: boolean }>(
    (state, message, index, messages) => {
      if (state.skip) return { items: state.items, skip: false };
      const next = messages[index + 1];
      const answered = new Set(next?.role === 'user' ? toolResultIds(next) : []);
      const missing = toolUseIds(message).filter((id) => !answered.has(id));
      if (missing.length === 0) return { items: [...state.items, message], skip: false };
      const stubs = missing.map(missingToolResult);
      if (next?.role === 'user') {
        return {
          items: [...state.items, message, { role: 'user', content: [...stubs, ...asBlocks(next.content)] }],
          skip: true,
        };
      }
      return { items: [...state.items, message, { role: 'user', content: stubs }], skip: false };
    },
    { items: [], skip: false }
  );
  return collapseRoles(paired.items);
}

export function asTranscript(value: unknown): MessageParam[] {
  if (!Array.isArray(value)) return [];
  return value.filter((message): message is MessageParam => {
    if (!message || typeof message !== 'object') return false;
    const role = (message as { role?: unknown }).role;
    return role === 'user' || role === 'assistant';
  });
}

/** A label PDF from create_label: its plain S3 link (the bucket is public, so it never expires) and file name. */
export type LabelLink = { url: string; name: string };

export type DisplayMessage = { role: 'user' | 'assistant'; content: string; labels?: LabelLink[] };

/** The label in a tool's JSON result (create_label's `labelS3Url` and `labelFilename`), or null. */
export function labelLinkOf(result: unknown): LabelLink | null {
  if (typeof result !== 'string') return null;
  try {
    const parsed = JSON.parse(result);
    const url: unknown = parsed?.labelS3Url;
    if (typeof url !== 'string' || !url) return null;
    const name: unknown = parsed.labelFilename;
    return { url, name: typeof name === 'string' && name ? name : 'Label.pdf' };
  } catch {
    return null;
  }
}

/** Slack only shows the reply text, so the server adds the label links itself instead of trusting Timmy to copy them. */
export function replyWithLabelLinks(text: string, labels: LabelLink[]): string {
  return [text, ...labels.map((label) => `[Open label PDF](${label.url})`)].filter(Boolean).join('\n\n');
}

function labelLinksOf(message: MessageParam): LabelLink[] {
  if (message.role !== 'user') return [];
  return asBlocks(message.content).flatMap((block) => {
    const label = block.type === 'tool_result' ? labelLinkOf(block.content) : null;
    return label ? [label] : [];
  });
}

/** Chat bubbles from a saved transcript. Labels from tool results go on the next reply from Timmy, so it never has to copy their links. */
export function transcriptToDisplay(value: unknown): DisplayMessage[] {
  const shown: DisplayMessage[] = [];
  let labels: LabelLink[] = [];
  asTranscript(value).forEach((message) => {
    if (message.role !== 'user' && message.role !== 'assistant') return;
    labels = [...labels, ...labelLinksOf(message)];
    const content = textOf(message.content).trim();
    if (!content) return;
    if (message.role === 'assistant' && labels.length) {
      shown.push({ role: 'assistant', content, labels });
      labels = [];
      return;
    }
    shown.push({ role: message.role, content });
  });
  return shown;
}
